import { ProcessedCertificate, COURSE_CONFIG } from '../types';
import type { CourseCertificateConfig } from '../services/courseConfigurationService';

/** English ordinal suffix: 1st, 2nd, 3rd, 4th, 11th, 21st, etc. */
export function getOrdinalSuffix(n: number): string {
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return 'th';
  switch (n % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/**
 * Add th/nd/rd/st to an unsuffixed "D Month YYYY" string (e.g. "7 September 2026").
 * Leaves already-suffixed or unrecognised text unchanged.
 */
export function applyOrdinalSuffixToFormattedDate(raw: string): string {
  const text = String(raw || '').trim();
  const match = text.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
  if (!match) return text;
  const day = Number(match[1]);
  return `${day}${getOrdinalSuffix(day)} ${match[2]} ${match[3]}`;
}

/** Format a Date in en-IE locale with ordinal day (e.g. "10th August 2026"). */
export function formatCertificateDateEnIE(date: Date): string {
  const day = date.getDate();
  const month = new Intl.DateTimeFormat('en-IE', { month: 'long' }).format(date);
  const year = date.getFullYear();
  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
}

/** Format a local calendar Date as YYYY-MM-DD (no UTC shift). */
export function formatLocalIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse YYYY-MM-DD or ISO into a Date at local midnight. */
export function parseLocalCalendarDate(raw: string): Date {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return new Date();
  const ymd = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]), 12, 0, 0);
  }
  const date = new Date(trimmed);
  if (isNaN(date.getTime())) return new Date();
  return date;
}

/** Parse the certificate's courseDate into a Date at local midday. */
function parseCourseDate(cert: ProcessedCertificate): Date {
  return parseLocalCalendarDate(cert.courseDate || '');
}

/**
 * Course date + validity months − 1 day (validity ends the day before the anniversary).
 * Prefers a stored `expiryDate` on the certificate when present.
 */
export function calculateCertificateExpiryDate(
  cert: ProcessedCertificate,
  validityMonths: number
): Date {
  const stored = (cert as ProcessedCertificate & { expiryDate?: string }).expiryDate;
  if (stored && String(stored).trim()) {
    return parseLocalCalendarDate(String(stored));
  }
  const base = parseCourseDate(cert);
  const expiry = new Date(base);
  expiry.setMonth(expiry.getMonth() + validityMonths);
  expiry.setDate(expiry.getDate() - 1);
  return expiry;
}

/** Compute YYYY-MM-DD expiry from a course date string (always recalculates, no stored override). */
export function computeExpiryIsoFromCourseDate(
  courseDate: string,
  validityMonths: number
): string {
  const base = parseLocalCalendarDate(courseDate);
  if (isNaN(base.getTime())) return '';
  const expiry = new Date(base);
  expiry.setMonth(expiry.getMonth() + validityMonths);
  expiry.setDate(expiry.getDate() - 1);
  return formatLocalIsoDate(expiry);
}

/** Resolve validity months from course config, static COURSE_CONFIG, or default 36. */
export function resolveValidityMonths(
  cert: ProcessedCertificate,
  courseConfig: CourseCertificateConfig | null
): number {
  if (courseConfig?.validityMonths) return courseConfig.validityMonths;
  const staticCfg = COURSE_CONFIG[cert.courseName as keyof typeof COURSE_CONFIG] as
    | { validityMonths?: number }
    | undefined;
  return staticCfg?.validityMonths || 36;
}

export interface CertificatePlaceholderInput {
  certificate: ProcessedCertificate;
  courseConfig: CourseCertificateConfig | null;
  certificateNumber: string;
  qrDataUrl: string;
}

export const CERTIFICATE_PLACEHOLDER_CHIPS: Array<{ token: string; description: string }> = [
  { token: '{{ContactName}}', description: 'Attendee name' },
  { token: '{{CompletionStatement}}', description: 'Course completion statement' },
  { token: '{{CourseName}}', description: 'Display name on certificate (else course name)' },
  { token: '{{training_description}}', description: 'Certificate topics (one line per topic)' },
  { token: '{{ComplianceStatement}}', description: 'Course compliance statement' },
  { token: '{{InstructorFirstName}}', description: 'Instructor first name' },
  { token: '{{InstructorLastName}}', description: 'Instructor last name' },
  { token: '{{CourseDate}}', description: 'Course date' },
  { token: '{{expiry_date}}', description: 'Expiry date' },
  { token: '{{certificate_number}}', description: 'Certificate number' },
  { token: '{{QR_Code}}', description: 'QR code image — use as an img src' },
];

/** Old seeds ended titles at "CERTIFICATE OF" and relied on a hardcoded TRAINING line. */
export function finalizeDisplayNameOnCertificate(name: string): string {
  const trimmed = (name || '').trim();
  if (!trimmed) return trimmed;
  const lines = trimmed.split(/\n/).map((l) => l.trimEnd());
  const lastIdx = lines.length - 1;
  const last = (lines[lastIdx] || '').trim();
  if (/^CERTIFICATE OF$/i.test(last)) {
    lines[lastIdx] = 'CERTIFICATE OF TRAINING';
    return lines.join('\n');
  }
  return lines.join('\n');
}

/** Build the map of {{Placeholder}} -> value used by the certificate HTML template. */
export function buildCertificatePlaceholderMap(
  input: CertificatePlaceholderInput
): Record<string, string> {
  const { certificate: cert, courseConfig, certificateNumber, qrDataUrl } = input;

  const instructorParts = (cert.instructorName || '').trim().split(/\s+/);
  const instructorFirstName = instructorParts[0] || '';
  const instructorLastName = instructorParts.slice(1).join(' ') || '';

  const courseDateFormatted = cert.courseDate
    ? formatCertificateDateEnIE(parseCourseDate(cert))
    : '';

  const validityMonths = resolveValidityMonths(cert, courseConfig);
  const expiryDate = calculateCertificateExpiryDate(cert, validityMonths);
  const expiryFormatted = formatCertificateDateEnIE(expiryDate);

  const completionStatement =
    courseConfig?.completionStatement ||
    (COURSE_CONFIG[cert.courseName as keyof typeof COURSE_CONFIG] as
      | { completionStatement?: string }
      | undefined)?.completionStatement ||
    'Has Successfully Attended and Completed';

  const trainingLines = (
    courseConfig?.additionalStaticText ||
    (COURSE_CONFIG[cert.courseName as keyof typeof COURSE_CONFIG] as
      | { additionalStaticText?: string[] }
      | undefined)?.additionalStaticText ||
    []
  )
    .map((line) => String(line || '').trim())
    .filter(Boolean);
  const trainingDescription = trainingLines.join('\n');

  const complianceStatement =
    courseConfig?.complianceStatement ||
    (COURSE_CONFIG[cert.courseName as keyof typeof COURSE_CONFIG] as
      | { complianceStatement?: string }
      | undefined)?.complianceStatement ||
    '';

  const displayName = finalizeDisplayNameOnCertificate(
    courseConfig?.displayNameOnCertificate ||
      (COURSE_CONFIG[cert.courseName as keyof typeof COURSE_CONFIG] as
        | { displayNameOnCertificate?: string }
        | undefined)?.displayNameOnCertificate ||
      '',
  );
  const courseName = displayName.trim() || (cert.courseName || '').trim();

  return {
    '{{CompletionStatement}}': completionStatement,
    '{{ContactName}}': cert.attendeeName || '',
    '{{CourseName}}': courseName,
    '{{training_description}}': trainingDescription,
    '{{ComplianceStatement}}': complianceStatement,
    '{{InstructorFirstName}}': instructorFirstName,
    '{{InstructorLastName}}': instructorLastName,
    '{{CourseDate}}': courseDateFormatted,
    '{{expiry_date}}': expiryFormatted,
    '{{certificate_number}}': certificateNumber,
    '{{QR_Code}}': qrDataUrl || '',
  };
}

/** Replace all {{Placeholder}} tokens in the HTML with their resolved values. */
export function replaceCertificatePlaceholders(
  html: string,
  placeholders: Record<string, string>
): string {
  let result = html;
  for (const [key, value] of Object.entries(placeholders)) {
    result = result.split(key).join(value || '');
  }
  return result;
}
